import { useEffect, useState, useCallback, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import { Horse, Trophy, MapPin, Warning, ChartLineUp, ChartBar, Target, HandCoins, SortAscending, CaretUp, CaretDown, Flame, Snowflake, CalendarBlank, PencilSimple, FloppyDisk, X, Plus, Minus, CircleNotch, Sparkle, Ruler, Medal, UsersThree, Drop, Lock, SignOut, CaretRight, LinkSimple, Barbell, CurrencyDollar } from "@phosphor-icons/react";
import { TrendingUp } from "lucide-react";
import { AdminPortal } from "./AdminPortal";
import { PushBell } from "./PushBell";
import { CalendarPicker, ViewingDateBanner } from "./CalendarPicker";
import { useAuth } from "./AuthContext";
import { AuthPage } from "./AuthPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "/ftp-logo.png";
// Public site refresh cadence. Lowered from 900s → 60s (2026-05-23)
// so ROI / strike-rate stats tick the moment a result is settled
// by the FormStack background ingest (which also runs every 60s).
const REFRESH_INTERVAL = 60;

// V7.4.8 — read the admin token left in localStorage by AdminPortal.
// If a token is present the public bet-slip cards expose inline
// edit controls (blurb / confidence / unit_stake).  Mirrors the
// key the AdminPortal uses ("ftp_admin_token") so the same login
// session unlocks both surfaces without re-auth.
const ADMIN_TOKEN_KEY = "ftp_admin_token";
function readAdminToken() {
  try { return localStorage.getItem(ADMIN_TOKEN_KEY) || null; }
  catch { return null; }
}

// ============== Utility ==============

function formatCountdown(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatShortDate(dateStr) {
  if (!dateStr) return "";
  const parts = dateStr.split(", ");
  return parts.length > 1 ? parts[1] : dateStr;
}

// Australian state → IANA timezone name. Using IANA + Intl.DateTimeFormat
// gives us correct DST handling and half-hour offsets (SA, NT) automatically.
const STATE_TZ = {
  "VIC": "Australia/Melbourne",
  "NSW": "Australia/Sydney",
  "ACT": "Australia/Sydney",
  "TAS": "Australia/Hobart",
  "QLD": "Australia/Brisbane",
  "SA":  "Australia/Adelaide",
  "NT":  "Australia/Darwin",
  "WA":  "Australia/Perth",
  "NZ":  "Pacific/Auckland",
};

// Returns the offset of `tz` from UTC, in minutes, at a given UTC instant.
// Positive = ahead of UTC.
function getOffsetMinutes(tz, atMs) {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const parts = fmt.formatToParts(new Date(atMs));
    const get = (t) => parseInt(parts.find(p => p.type === t).value, 10);
    let hour = get("hour");
    if (hour === 24) hour = 0; // some engines emit "24" at midnight
    const trackUtc = Date.UTC(
      get("year"), get("month") - 1, get("day"),
      hour, get("minute"), get("second"),
    );
    return Math.round((trackUtc - atMs) / 60000);
  } catch {
    return 600; // safe default = AEST
  }
}

// Convert a race-time HH:MM (in the track's local timezone) into the
// viewer's local-clock HH:MM. Handles DST and half-hour offsets correctly.
function raceTimeToLocal(raceTime, state) {
  if (!raceTime || !raceTime.includes(":")) return raceTime;
  const [h, m] = raceTime.split(":").map(Number);
  const tz = STATE_TZ[state] || "Australia/Melbourne";
  const now = Date.now();
  const trackOffsetMin = getOffsetMinutes(tz, now);
  // Today's calendar date in track-local time:
  const trackNow = new Date(now + trackOffsetMin * 60000);
  const raceUtcMs = Date.UTC(
    trackNow.getUTCFullYear(),
    trackNow.getUTCMonth(),
    trackNow.getUTCDate(),
    h, m, 0,
  ) - trackOffsetMin * 60000;
  const raceDate = new Date(raceUtcMs);
  const hh = String(raceDate.getHours()).padStart(2, "0");
  const mm = String(raceDate.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function getCountdownToRace(raceTime, state, dateString) {
  if (!raceTime || !raceTime.includes(":")) return null;
  const [h, m] = raceTime.split(":").map(Number);
  const tz = STATE_TZ[state] || "Australia/Melbourne";
  const now = Date.now();
  const trackOffsetMin = getOffsetMinutes(tz, now);

  // Parse the race DATE (DD/MM/YYYY). If absent, fall back to "today at the track"
  // — preserves old behaviour for live cards without a date_string.
  let raceY, raceM, raceD;
  if (dateString && /^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const [d, mo, y] = dateString.split("/").map(Number);
    raceY = y; raceM = mo - 1; raceD = d;
  } else {
    const trackNow = new Date(now + trackOffsetMin * 60000);
    raceY = trackNow.getUTCFullYear();
    raceM = trackNow.getUTCMonth();
    raceD = trackNow.getUTCDate();
  }
  const raceUtcMs = Date.UTC(raceY, raceM, raceD, h, m, 0) - trackOffsetMin * 60000;
  const diffMs = raceUtcMs - now;
  if (diffMs < 0) return null; // Already passed
  const mins = Math.floor(diffMs / 60000);
  if (mins > 240) return null; // More than 4 hours away — don't bother showing
  if (mins < 1) return "LIVE";
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// ============== Custom Hook ==============

function useSelections() {
  const [selections, setSelections] = useState([]);
  const [bestBet, setBestBet] = useState(null);
  const [valueBets, setValueBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState("");
  const [melbourneDate, setMelbourneDate] = useState("");
  const [nextRefresh, setNextRefresh] = useState(REFRESH_INTERVAL);
  const [refreshing, setRefreshing] = useState(false);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [ytdStats, setYtdStats] = useState(null);
  // Calendar — when null we're on Today (live auto-refresh); when set we're
  // browsing a different day (no live refresh).
  const [viewingDay, setViewingDay] = useState(null);

  const fetchSelections = useCallback(async (overrideDate) => {
    try {
      setError(null);
      // Backend now sets Cache-Control: public, max-age=15, stale-while-revalidate=30
      // and dedupes via an in-process 25s TTL. Drop the per-request
      // timestamp cache-buster so the browser HTTP cache + CDN can
      // serve repeat requests instantly during PWA refocus + 60s
      // polling (2026-06-02: user reported "starts a fresh analysis
      // every time the app is opened — extremely slow").
      const params = overrideDate ? { date: overrideDate } : {};
      const response = await axios.get(`${API}/selections`, { params });
      const data = response.data;
      setSelections(data.selections || []);
      setBestBet(data.best_bet);
      setValueBets(data.value_bets || []);
      setLastUpdated(data.last_updated);
      setMelbourneDate(data.melbourne_date);
      setNextRefresh(data.next_refresh_seconds || REFRESH_INTERVAL);
      setWeeklyStats(data.weekly_stats);
      setYtdStats(data.ytd_stats);
      setLoading(false);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error fetching selections:", err);
      }
      setError("Failed to fetch selections");
      setLoading(false);
    }
  }, []);

  const handleSync = useCallback(async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/sync-now`);
      let attempts = 0;
      while (attempts < 40) {
        await new Promise(r => setTimeout(r, 3000));
        const res = await axios.get(`${API}/sync-status`);
        if (!res.data.is_syncing) break;
        attempts++;
      }
      await fetchSelections(viewingDay?.date);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error syncing:", err);
      }
    }
    setRefreshing(false);
  }, [fetchSelections, viewingDay]);

  const selectDay = useCallback((day) => {
    if (!day || day.is_today) {
      setViewingDay(null);
      fetchSelections();
    } else {
      setViewingDay(day);
      fetchSelections(day.date);
    }
  }, [fetchSelections]);

  useEffect(() => { fetchSelections(); }, [fetchSelections]);

  // Re-fetch the moment the tab/PWA becomes visible again. PWAs in particular
  // tend to keep stale state for a long time when backgrounded; a focus/visibility
  // refresh guarantees the user never sees old data after re-opening the app.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") {
        fetchSelections(viewingDay?.date);
      }
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
    };
  }, [fetchSelections, viewingDay]);

  useEffect(() => {
    // Live auto-refresh only on Today. When browsing a different day the
    // dashboard is a static snapshot.
    if (viewingDay) return;
    const timer = setInterval(() => {
      setNextRefresh((prev) => {
        if (prev <= 1) { fetchSelections(); return REFRESH_INTERVAL; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [fetchSelections, viewingDay]);

  return {
    selections, bestBet, valueBets, loading, error, lastUpdated,
    melbourneDate, nextRefresh, refreshing, weeklyStats, ytdStats,
    viewingDay, selectDay,
    fetchSelections, handleSync,
  };
}

// ============== Sub-components ==============

// Replaces a gated card's body when logged out -- matches the lock-row
// pattern used consistently across AI Scan / Race Signals / Live Edge /
// Market Movers.
const LoginGate = ({ feature, onLoginClick }) => (
  <div className="login-gate" data-testid={`login-gate-${feature.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="login-gate-icon"><Lock size={18} weight="fill" /></div>
    <div className="login-gate-text">
      <span className="login-gate-title">{feature}</span>
      <span className="login-gate-subtitle">Available when logged in</span>
    </div>
    <button type="button" className="login-gate-button" data-testid="login-gate-button" onClick={onLoginClick}>
      Log in to view<CaretRight size={12} weight="bold" />
    </button>
  </div>
);

const LoadingCard = () => (
  <div className="loading-card" data-testid="loading-card">
    <div className="loading-header"><div className="loading-line medium"></div><div className="loading-line short"></div></div>
    <div className="loading-line long"></div><div className="loading-line full"></div><div className="loading-line medium"></div>
  </div>
);

const IntroCard = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div data-testid="intro-card" style={{
      background: "#fff", border: "1px solid #eceae7", borderRadius: 10,
      padding: "16px 18px", marginBottom: 12,
    }}>
      <p style={{
        fontFamily: "'Manrope', sans-serif", fontSize: 13, lineHeight: 1.7,
        color: "#323233", margin: 0, letterSpacing: "0.005em",
      }}>
        Precision-driven racing analysis built to uncover market inefficiencies in Australian horse racing,
        utilising the power of artificial intelligence.
      </p>
      {expanded && (
        <div data-testid="intro-extra" style={{
          fontSize: 13, lineHeight: 1.7, color: "#555",
          marginTop: 10, paddingTop: 12, borderTop: "1px solid #f2f0ed",
        }}>
          <p style={{ margin: "0 0 10px" }}>
            The model continuously processes large-scale data to identify pricing edges —
            removing emotion, bias, and guesswork from every decision.
          </p>
          <p style={{ margin: "0 0 10px" }}>
            FTP Form Edge is designed to identify where the market gets it wrong.
          </p>
          <p style={{ margin: "0 0 10px" }}>
            Selections are only released when a measurable edge is present. If the market is efficient,
            no bets are advised.
          </p>
          <p style={{ margin: 0 }}>
            This is a disciplined, data-first approach focused on consistency, not volume —
            built to operate without emotion and aligned purely around finding and capturing value.
          </p>
        </div>
      )}
      <button
        data-testid="intro-toggle"
        onClick={() => setExpanded(!expanded)}
        style={{
          background: "none", border: "none", padding: "8px 0 0", marginTop: 4,
          color: "#323233", fontFamily: "'Manrope', sans-serif",
          fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
          textTransform: "uppercase", cursor: "pointer", opacity: 0.65,
          transition: "opacity 0.2s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.65; }}
      >
        {expanded ? "Show Less" : "Read More"}
      </button>
    </div>
  );
};

const formatJumpCountdown = (ms) => {
  if (ms <= 0) return "0:00";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
};

const NextToJumpCard = () => {
  const [races, setRaces] = useState([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let active = true;
    const fetchRaces = () => {
      axios.get(`${API}/next_to_jump`).then((res) => {
        if (active) setRaces(res.data.races || []);
      }).catch(() => {});
    };
    fetchRaces();
    // Re-sync against the server hourly so a delayed/rescheduled jump
    // time never drifts out of truth for long — the on-screen tick
    // itself still updates every second locally in between.
    const interval = setInterval(fetchRaces, 3600000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  const upcoming = races.filter((r) => new Date(r.race_time).getTime() > now);
  if (upcoming.length === 0) return null;

  return (
    <div className="ntj-card" data-testid="next-to-jump-card">
      <div className="ntj-header">UP NEXT</div>
      <div className="ntj-list">
        {upcoming.map((r, i) => (
          <div key={`${r.track}-${r.race_number}`}
            className={`ntj-row ${i === 0 ? "ntj-row-featured" : ""}`}
            data-testid="next-to-jump-row">
            <span className="ntj-name">{r.track} R{r.race_number}</span>
            <span className="ntj-time" data-testid="next-to-jump-time">
              {formatJumpCountdown(new Date(r.race_time).getTime() - now)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};


const UnitStakingExplainer = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div data-testid="unit-staking-card" style={{
      background: "#fafaf9", border: "1px solid #eceae7", borderRadius: 10,
      padding: "14px 18px", margin: "16px 0",
    }}>
      <button
        data-testid="unit-staking-toggle"
        onClick={() => setExpanded(!expanded)}
        style={{
          background: "none", border: "none", padding: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          width: "100%", cursor: "pointer", color: "#323233",
          fontFamily: "'Manrope', sans-serif",
        }}
      >
        <span style={{
          fontSize: 12, fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}>
          What is unit staking?
        </span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
          textTransform: "uppercase", opacity: 0.55,
        }}>
          {expanded ? "Hide" : "Read More"}
        </span>
      </button>
      {expanded && (
        <div data-testid="unit-staking-body" style={{
          fontSize: 13, lineHeight: 1.7, color: "#555",
          marginTop: 12, paddingTop: 12, borderTop: "1px solid #eceae7",
        }}>
          <p style={{ margin: "0 0 10px" }}>
            A unit is a standardised way to measure bet size, allowing results to be tracked consistently
            regardless of bankroll, and is typically calculated as a percentage of your total bankroll.
          </p>
          <p style={{ margin: "0 0 10px" }}>
            A common approach is to set 1 unit equal to 1–2% of your bankroll, ensuring disciplined staking
            and protection against losing runs. For example, if your bankroll is $1,000, then 1 unit would
            equal $10–$20, meaning a 2-unit bet would be $20–$40 — allowing you to scale staking while
            maintaining the same risk profile and strategy.
          </p>
          <p style={{
            margin: 0, paddingTop: 10, borderTop: "1px solid #eceae7",
            fontSize: 11, color: "#999", lineHeight: 1.6, fontStyle: "italic",
          }}>
            Unit staking is a general guideline only and does not consider individual financial situations
            or objectives. All betting involves risk, and users are responsible for setting their own unit
            size and managing their bankroll accordingly. The information provided is for informational and
            entertainment purposes only and should not be considered financial or betting advice. Past
            performance is not indicative of future results, and no guarantees are made regarding accuracy
            or profitability.
          </p>
        </div>
      )}
    </div>
  );
};

const EmptyState = () => (
  <div data-testid="empty-state" style={{
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "36px 20px", margin: "8px 0",
    background: "#fafaf9", borderRadius: 10,
    border: "1px solid #f0f0ef",
    textAlign: "center",
  }}>
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      width: 44, height: 44, borderRadius: 999,
      background: "#f0f0ef", marginBottom: 14,
    }}>
      <Horse size={22} weight="regular" color="#999" />
    </div>
    <h3 style={{ fontSize: 15, fontWeight: 700, color: "#323233", margin: "0 0 8px" }}>
      No Selections Today
    </h3>
    <p style={{ fontSize: 12, color: "#888", margin: "0 0 6px", maxWidth: 320, lineHeight: 1.6 }}>
      The model did not identify a clear edge in today's markets.
    </p>
    <p style={{ fontSize: 12, color: "#aaa", margin: 0, maxWidth: 320, lineHeight: 1.6 }}>
      When no advantage exists, no bets are taken.
      <br />
      Continuously scanning — check back soon.
    </p>
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className="error-container" data-testid="error-state">
    <div className="error-icon">
      <Warning size={44} weight="bold" />
    </div>
    <h3 className="error-title">Connection Error</h3>
    <p className="error-text">Unable to fetch racing data. Please check your connection and try again.</p>
    <button className="retry-button" onClick={onRetry} data-testid="retry-button">Try Again</button>
  </div>
);

const UnitsRow = ({ stats, idPrefix }) => {
  const units = Number(stats.units_profit ?? 0);
  const roi = Number(stats.roi_pct ?? 0);
  const positive = units >= 0;
  const sign = positive ? "+" : "";
  const color = "#ffffff";
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr 1fr",
      gap: 8, marginTop: 8,
    }}>
      <div className="stat-item" style={{ padding: "10px 12px" }}>
        <span className="stat-value" data-testid={`${idPrefix}-units`}
          style={{ color, fontVariantNumeric: "tabular-nums" }}>
          {sign}{units.toFixed(2)}u
        </span>
        <span className="stat-label">Units</span>
      </div>
      <div className="stat-item" style={{ padding: "10px 12px" }}>
        <span className="stat-value" data-testid={`${idPrefix}-roi`}
          style={{ color, fontVariantNumeric: "tabular-nums" }}>
          {sign}{roi.toFixed(1)}%
        </span>
        <span className="stat-label">ROI</span>
      </div>
    </div>
  );
};

// ============== AI Scan ==============

function formatRaceTimeLocal(isoUtc, state) {
  if (!isoUtc) return "";
  try {
    const tz = STATE_TZ[state] || "Australia/Melbourne";
    const d = new Date(isoUtc);
    const fmt = new Intl.DateTimeFormat("en-AU", { timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true });
    return fmt.format(d).replace(/\s/g, "").toLowerCase();
  } catch { return ""; }
}

function ddmmyyyyToIso(s) {
  if (!s) return null;
  const [d, m, y] = s.split("/");
  return `${y}-${m}-${d}`;
}

const SCAN_STEPS = [
  "Analysing race conditions…",
  "Checking the market…",
  "Ranking the contenders…",
  "Predictions ready…",
];

const confBandTier = (band) => (
  band === "EXTREME" || band === "VERY HIGH" || band === "HIGH" ? "high" :
  band === "MEDIUM" ? "med" : "low"
);

const ScanLoader = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (step >= SCAN_STEPS.length - 1) return;
    const t = setTimeout(() => setStep((s) => s + 1), 500);
    return () => clearTimeout(t);
  }, [step]);
  return (
    <div className="ai-scan-loader" data-testid="ai-scan-loader">
      <CircleNotch size={18} weight="bold" className="ai-scan-spinner" />
      <span data-testid="ai-scan-loader-text">{SCAN_STEPS[step]}</span>
    </div>
  );
};

const AI_BADGE_CONFIG = {
  "won easy": { tier: "gold", icon: Flame, rank: 1 },
  "lightly raced": { tier: "gold", icon: CalendarBlank, rank: 2 },
  "trainer at track": { tier: "grey", icon: UsersThree, rank: 3 },
  "track specialist": { tier: "grey", icon: Medal, rank: 4 },
  "beaten narrow": { tier: "grey", icon: Target, rank: 5 },
  "consistent winner": { tier: "grey", icon: ChartLineUp, rank: 6 },
  "strong combo": { tier: "grey", icon: Sparkle, rank: 7 },
  "jockey at track": { tier: "grey", icon: MapPin, rank: 8 },
  "emerging talent": { tier: "white", icon: TrendingUp, rank: 9 },
  "last start winner": { tier: "white", icon: Trophy, rank: 10 },
  "consistent placer": { tier: "white", icon: ChartBar, rank: 11 },
  "course & distance": { tier: "white", icon: Ruler, rank: 12 },
  // V7.5.38 (2026-10-31) — display-only, shown only when today's
  // track condition is soft/heavy (see `isWetCondition` at call
  // sites below). No score weight: deliberately absent from
  // _SELECTION_BADGE_POINTS on the backend.
  "likes wet tracks": { tier: "white", icon: Drop, rank: 13 },
};

const isWetCondition = (condition) => {
  const c = (condition || "").trim().toLowerCase().split(" ")[0];
  return c === "soft" || c === "heavy";
};

const aiDisplayBadges = (badges, isWet) => (badges || [])
  .map((b) => ({ raw: b, cfg: AI_BADGE_CONFIG[(b || "").trim().toLowerCase()] }))
  .filter((x) => x.cfg && (x.raw.trim().toLowerCase() !== "likes wet tracks" || isWet))
  .sort((a, b) => a.cfg.rank - b.cfg.rank);

const ScanPickRow = ({ pick, rank, tierLabel, hideConfidence }) => {
  const tier = tierLabel ? "charcoal" : rank === 1 ? "gold" : rank === 2 ? "silver" : "bronze";
  return (
    <div className={`ai-pick-card ${tier}`} data-testid={`ai-scan-pick-${rank}`}>
      {tierLabel && (
        <div className="ai-pick-ribbon" data-testid={`ai-scan-pick-tier-${rank}`}>{tierLabel}</div>
      )}
      <div className="ai-pick-top-row">
        {pick.silk_url
          ? <img className="ai-pick-silk" src={pick.silk_url} alt="" />
          : <div className="ai-pick-silk-placeholder" />}
        <div className="ai-pick-main">
          <div className="ai-pick-name-row">
            <span className="ai-pick-name" data-testid={`ai-scan-pick-name-${rank}`}>
              #{pick.tab_number} {pick.runner_name}
            </span>
            {Array.from({ length: pick.flame_count || 0 }).map((_, i) => (
              <span key={i} className="ai-pick-emoji" title="Notable market support" data-testid={`ai-scan-flame-${rank}`}>🔥</span>
            ))}
            {Array.from({ length: pick.puff_count || 0 }).map((_, i) => (
              <span key={i} className="ai-pick-emoji" title="Margin to next favourite" data-testid={`ai-scan-smoke-${rank}`}>💨</span>
            ))}
          </div>
          <div className="ai-pick-subline">J: {pick.jockey || "TBA"} &nbsp;|&nbsp; T: {pick.trainer || "TBA"}</div>
          <div className="ai-pick-subline">
            Bar {pick.barrier ?? "-"} · {pick.weight ?? "-"}kg{pick.age ? ` · ${pick.age}yo` : ""}{pick.running_style ? ` · ${pick.running_style}` : ""}
          </div>
        </div>
        <div className="ai-pick-right">
          <span className="ai-pick-tab" data-testid={`ai-scan-tab-price-${rank}`}>
            {pick.tab_price != null ? `$${pick.tab_price.toFixed(2)}` : "—"}
          </span>
          {pick.opening_price != null && (
            <span className="ai-pick-open" data-testid={`ai-scan-open-price-${rank}`}>Opened ${pick.opening_price.toFixed(2)}</span>
          )}
          {pick.finish_position && (
            <span className={`ai-pick-finish tier-${pick.finish_position <= 3 ? pick.finish_position : "other"}`} data-testid={`ai-scan-finish-${rank}`}>
              {pick.finish_position}{pick.finish_position === 1 ? "ST" : pick.finish_position === 2 ? "ND" : pick.finish_position === 3 ? "RD" : "TH"}
            </span>
          )}
        </div>
      </div>
      {pick.badges && pick.badges.length > 0 && (
        <div className="ai-pick-badges">
          {aiDisplayBadges(pick.badges, isWetCondition(pick.condition)).map(({ raw, cfg }, i) => {
            const Icon = cfg.icon;
            return (
              <span key={i} className={`ai-pick-badge-chip badge-tier-${cfg.tier}`}>
                <Icon size={8} weight="bold" />{raw}
              </span>
            );
          })}
        </div>
      )}
      {pick.display_confidence != null && !hideConfidence && (
        <div className="confidence-bar ai-pick-confidence-bar" data-testid={`ai-scan-confidence-bar-${rank}`}>
          <div className="confidence-bar-fill high" style={{ width: `${pick.display_confidence}%` }}></div>
        </div>
      )}
    </div>
  );
};

const AIScanCard = ({ dateIso, isAuthenticated, onLoginClick }) => {
  const [meetings, setMeetings] = useState([]);
  const [meetingsLoaded, setMeetingsLoaded] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [track, setTrack] = useState("");
  const [race, setRace] = useState("");
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [winOfDay, setWinOfDay] = useState(null);

  useEffect(() => {
    setTrack(""); setRace(""); setPhase("idle"); setResult(null); setMeetingsLoaded(false);
    const params = dateIso ? { date: dateIso } : {};
    axios.get(`${API}/race/meetings`, { params }).then((res) => {
      setMeetings(res.data.meetings || []);
    }).catch(() => setMeetings([])).finally(() => setMeetingsLoaded(true));
  }, [dateIso]);

  useEffect(() => {
    let active = true;
    const fetchWin = () => {
      const params = dateIso ? { date: dateIso } : {};
      axios.get(`${API}/ai_scan/win_of_day`, { params }).then((res) => {
        if (active) setWinOfDay(res.data.pick || null);
      }).catch(() => { if (active) setWinOfDay(null); });
    };
    fetchWin();
    const interval = setInterval(fetchWin, 120000);
    return () => { active = false; clearInterval(interval); };
  }, [dateIso]);

  const trackMeta = useMemo(() => meetings.find((m) => m.slug === track), [meetings, track]);
  const races = trackMeta ? trackMeta.races : [];

  useEffect(() => {
    if (!track || !race) { setPhase("idle"); setResult(null); return; }
    setPhase("loading");
    setResult(null);
    const minLoad = new Promise((r) => setTimeout(r, 2400));
    const params = dateIso ? { track, race, date: dateIso } : { track, race };
    const fetchPromise = axios.get(`${API}/race/scan`, { params });
    Promise.all([fetchPromise, minLoad]).then(([res]) => {
      if (res.data.error || !res.data.picks || res.data.picks.length === 0) {
        setPhase("error");
      } else {
        setResult(res.data);
        setPhase("done");
      }
    }).catch(() => setPhase("error"));
  }, [track, race, dateIso]);

  const raceLabel = trackMeta ? `${trackMeta.track} R${race}` : "";
  const [guideOpen, setGuideOpen] = useState(false);

  if (meetingsLoaded && meetings.length === 0) return null;

  const analysisGuide = (
    <div className="analysis-guide-container" data-testid="analysis-guide-container">
      <button type="button" data-testid="analysis-guide-toggle" onClick={() => setGuideOpen((g) => !g)}
        style={{ background: "none", border: "none", cursor: "pointer", color: "#323233",
          fontSize: 12, fontWeight: 600, padding: "4px 0", letterSpacing: "0.03em",
          opacity: 0.6, transition: "opacity 0.2s" }}
        onMouseEnter={(e) => { e.target.style.opacity = 1; }}
        onMouseLeave={(e) => { e.target.style.opacity = 0.6; }}
      >{guideOpen ? "Hide Analysis Guide" : "Analysis Guide"}</button>
      {guideOpen && (
        <div data-testid="analysis-guide-body" style={{
          fontSize: 12, lineHeight: 1.7, color: "#666", margin: "6px 0 0", padding: "8px 10px",
          background: "#f9f9f8", borderRadius: 6, borderLeft: "2px solid #ddd",
        }}>
          <p style={{ margin: "0 0 2px", fontWeight: 700, color: "#323233" }}>The full picture</p>
          <p style={{ margin: "0 0 10px" }}>The analysis brings several signals together. Don’t judge a runner on one stat alone; each rating, badge and market indicator contributes to the overall assessment.</p>
          <p style={{ margin: "0 0 2px", fontWeight: 700, color: "#323233" }}>Race Signal</p>
          <p style={{ margin: "0 0 10px" }}>Shows the overall strength of our analysis for a race. A higher signal means there is more available data and as such, clearer, stronger profiles in the field. It’s an assessment of the overall race.</p>
          <p style={{ margin: "0 0 2px", fontWeight: 700, color: "#323233" }}>Green bars</p>
          <p style={{ margin: "0 0 10px" }}>Show each runner’s overall rating. The longer the bar, the stronger our assessment of that horse.</p>
          <p style={{ margin: "0 0 2px", fontWeight: 700, color: "#323233" }}>Badges</p>
          <p style={{ margin: "0 0 10px" }}>Highlight positive form indicators, such as recent wins, consistent placings and strong jockey or track combinations. They’re colour coded: gold is strongest, followed by silver, then white.</p>
          <p style={{ margin: "0 0 2px", fontWeight: 700, color: "#323233" }}>🔥 Fire symbol</p>
          <p style={{ margin: "0 0 10px" }}>Marks runners that have shortened in the betting market from their opening price. They show market support, but don’t guarantee a result.</p>
          <p style={{ margin: "0 0 2px", fontWeight: 700, color: "#323233" }}>💨 Smoke symbol</p>
          <p style={{ margin: 0 }}>Shows the rating gap between that runner and the next-best-rated horse. More smoke means a bigger gap.</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="ai-scan-card" data-testid="ai-scan-card">
      <button type="button" className="ai-scan-toggle" data-testid="ai-scan-toggle" onClick={() => setExpanded((e) => !e)}>
        <span className="ai-scan-toggle-left">
          <span aria-hidden="true">✦</span>
          Ai RACE PREDICTIONS
        </span>
        <span className="ai-scan-toggle-right">
          {expanded ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
        </span>
      </button>

      {expanded && (
        <div className="ai-scan-body">
          {!isAuthenticated ? (
            <LoginGate feature="AI Race Predictions" onLoginClick={onLoginClick} />
          ) : (
            <>
              <div className="ai-scan-pickers">
                <div className="ai-picker-box">
                  <MapPin size={16} weight="fill" />
                  <div className="ai-picker-text">
                    <span className="ai-picker-label">Track</span>
                    <span className="ai-picker-value">{trackMeta ? trackMeta.track : "Select Track"}</span>
                  </div>
                  <CaretDown size={12} weight="bold" className="ai-picker-caret" />
                  <select className="ai-picker-native" data-testid="ai-scan-track-select" value={track}
                    onChange={(e) => { setTrack(e.target.value); setRace(""); }}>
                    <option value="">Select Track</option>
                    {meetings.map((m) => <option key={m.slug} value={m.slug}>{m.track}</option>)}
                  </select>
                </div>
                <div className={`ai-picker-box ${!track ? "disabled" : ""}`}>
                  <Trophy size={16} weight="fill" />
                  <div className="ai-picker-text">
                    <span className="ai-picker-label">Race</span>
                    <span className="ai-picker-value">{race ? `R${race}` : "Select Race No."}</span>
                  </div>
                  <CaretDown size={12} weight="bold" className="ai-picker-caret" />
                  <select className="ai-picker-native" data-testid="ai-scan-race-select" value={race}
                    disabled={!track} onChange={(e) => setRace(e.target.value)}>
                    <option value="">Select Race No.</option>
                    {races.map((r) => <option key={r.race_number} value={r.race_number}>R{r.race_number}</option>)}
                  </select>
                </div>
              </div>

              {phase !== "done" && analysisGuide}

              {phase === "loading" && <ScanLoader />}
              {phase === "error" && (
                <p className="ai-scan-empty" data-testid="ai-scan-error">No AI Scan available for this race yet.</p>
              )}
              {phase === "done" && result && (
                <>
                  <div className="ai-scan-race-line">
                    <span className="ai-scan-race-title" data-testid="ai-scan-race-title">{raceLabel}</span>
                    <span className={`ai-scan-conf-group ${confBandTier(result.data_strength_band)}`}
                      data-testid="ai-scan-race-confidence-label"
                      title="Data Strength measures how clearly the data backs pick 1 over the other two picks on this card, from each pick's own FTP score and net form badges. It is a confidence index, not a predicted win probability.">
                      <span className="ai-scan-conf-group-label">DATA STRENGTH</span>
                      <span className="ai-scan-conf-group-value">{result.data_strength_band}</span>
                    </span>
                  </div>
                  <div className="ai-scan-meta-line" data-testid="ai-scan-meta">
                    {[
                      formatRaceTimeLocal(result.race_time, result.state),
                      result.distance,
                      result.race_class,
                      result.condition,
                      result.field_size ? `${result.field_size} runners` : null,
                    ].filter(Boolean).join(" · ")}
                    {result.race_pace && (
                      <>
                        {" · "}
                        <span className="ai-scan-pace-tag" data-testid="ai-scan-race-pace"
                          title="How much early speed is in this field.">
                          Pace: {result.race_pace}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="ai-scan-results" data-testid="ai-scan-results">
                    {result.picks.map((p, i) => <ScanPickRow key={`${p.tab_number}-${i}`} pick={p} rank={i + 1} tierLabel={p.tier} />)}
                  </div>
                  <div className="ai-scan-legend" data-testid="ai-scan-legend">
                    <span>🔥 = Notable market support</span>
                    <span className="ai-scan-legend-divider">|</span>
                    <span>💨 = Margin to next favourite</span>
                  </div>
                  {analysisGuide}
                </>
              )}

              {winOfDay && (
                <div className="ai-win-of-day" data-testid="ai-win-of-day">
                  <h4 className="ai-win-heading">Ai WIN OF THE DAY</h4>
                  <ScanPickRow pick={winOfDay} rank={1} hideConfidence />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============== Market Movers ==============

function finishSuffix(pos) {
  return pos === 1 ? "ST" : pos === 2 ? "ND" : pos === 3 ? "RD" : "TH";
}

function getCountdownFromIso(isoUtc) {
  if (!isoUtc) return null;
  const diffMs = new Date(isoUtc).getTime() - Date.now();
  if (diffMs < 0) return null;
  const mins = Math.floor(diffMs / 60000);
  if (mins > 240) return null;
  if (mins < 1) return "LIVE";
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

const MoverCountdown = ({ isoUtc }) => {
  const [c, setC] = useState(() => getCountdownFromIso(isoUtc));
  useEffect(() => {
    const t = setInterval(() => setC(getCountdownFromIso(isoUtc)), 30000);
    return () => clearInterval(t);
  }, [isoUtc]);
  if (!c) return null;
  const isUrgent = c === "LIVE" || /^\d+m$/.test(c);
  return (
    <span data-testid="mover-countdown" style={{
      fontSize: 11, fontWeight: 600,
      color: isUrgent ? "#e53e3e" : "#888",
      background: isUrgent ? "#fee2e2" : "#f5f5f4",
      padding: "1px 6px", borderRadius: 4,
    }}>{c}</span>
  );
};

const MarketMoverRow = ({ m }) => (
  <div className="mm-row" data-testid={`market-mover-row-${m.band}`}>
    {m.silk_url ? <img className="mm-silk" src={m.silk_url} alt="" /> : <div className="mm-silk mm-silk-placeholder" />}
    <div className="mm-main">
      <span className="mm-name" data-testid={`market-mover-name-${m.band}`}>#{m.tab_number} {m.runner_name}</span>
      <div className="mm-subline">{m.track} R{m.race_number}</div>
    </div>
    <div className="mm-price-col">
      <span className="mm-price-move" data-testid={`market-mover-price-${m.band}`}>
        <span className="mm-open">${m.opening_price.toFixed(2)}</span> → <span className="mm-current">${m.current_price.toFixed(2)}</span>
      </span>
      <MoverCountdown isoUtc={m.race_time} />
    </div>
    <div className="mm-firmed-col">
      <span className="mm-pct-chip" data-testid={`market-mover-pct-${m.band}`}>
        <CaretDown size={9} weight="bold" />-{m.pct_firmed}%
      </span>
      {m.trajectory_label && (
        <span className="mm-trajectory" data-testid={`market-mover-trajectory-${m.band}`}>{m.trajectory_label}</span>
      )}
      {m.finish_position && (
        <span className={`ai-pick-finish tier-${m.finish_position <= 3 ? m.finish_position : "other"}`}
          data-testid={`market-mover-finish-${m.band}`}>
          {m.finish_position}{finishSuffix(m.finish_position)}
        </span>
      )}
    </div>
  </div>
);

const MoversTable = ({ title, list, rightControl }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? list : list.slice(0, 3);
  const key = title.toLowerCase();
  return (
    <div className="mm-section" data-testid={`market-movers-section-${key}`}>
      <div className="mm-section-head">
        <span className="mm-section-title">{title}</span>
        {rightControl}
      </div>
      {list.length === 0 ? (
        <p className="ai-scan-empty" data-testid={`market-movers-empty-${key}`}>No {key} movers yet today.</p>
      ) : (
        <>
          <div className="mm-table-head">
            <span></span><span>HORSE</span><span className="mm-th-right">OPEN → TAB</span><span className="mm-th-right">FIRMED</span>
          </div>
          <div className="mm-list">
            {visible.map((m, i) => (
              <MarketMoverRow key={`${m.band}-${m.tab_number}-${m.race_number}-${i}`} m={m} />
            ))}
          </div>
          {list.length > 3 && (
            <button type="button" className="mm-expand-btn" data-testid={`market-movers-expand-${key}`}
              onClick={() => setShowAll((s) => !s)}>
              {showAll ? "Show less" : "Show more"}
              {showAll ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
            </button>
          )}
        </>
      )}
    </div>
  );
};

const RankArrow = ({ m, testId }) => (
  <span className={`rs-arrow-col ${m.rank_moved ? "rs-arrow-bright" : "rs-arrow-dim"}`} data-testid={testId}>
    {m.rank_arrow === "up" && <CaretUp size={10} weight="bold" className="rs-arrow-up" />}
    {m.rank_arrow === "down" && <CaretDown size={10} weight="bold" className="rs-arrow-down" />}
    {(!m.rank_arrow || m.rank_arrow === "neutral") && <Minus size={10} weight="bold" />}
  </span>
);

const TrajectoryIcon = ({ trajectory, testId }) => {
  if (trajectory === "shortening") return <CaretDown size={11} weight="fill" className="rs-traj-up" data-testid={testId} />;
  if (trajectory === "easing") return <CaretUp size={11} weight="fill" className="rs-traj-down" data-testid={testId} />;
  if (trajectory === "holding") return <Minus size={10} weight="bold" className="rs-traj-neutral" data-testid={testId} />;
  return null;
};

const RaceSignalRow = ({ m }) => (
  <div className="mm-row rs-row" data-testid="race-signal-row">
    <RankArrow m={m} testId="race-signal-arrow" />
    {m.silk_url ? <img className="mm-silk" src={m.silk_url} alt="" /> : <div className="mm-silk mm-silk-placeholder" />}
    <div className="mm-main">
      <span className="mm-name" data-testid="race-signal-name">#{m.tab_number} {m.runner_name}</span>
      <div className="mm-subline">{m.track} R{m.race_number}</div>
    </div>
    <div className="rs-signal-col">
      <span className="rs-signal-value" data-testid="race-signal-strength">{m.race_signal_strength}</span>
      <span className="rs-band-chip" data-testid="race-signal-band">{m.race_signal_band}</span>
    </div>
    <div className="mm-firmed-col">
      <span className="rs-price-row">
        <span className="rs-price-text" data-testid="race-signal-price">${m.tab_price.toFixed(2)}</span>
        <TrajectoryIcon trajectory={m.trajectory} testId="race-signal-trajectory" />
      </span>
      <MoverCountdown isoUtc={m.race_time} />
      {m.finish_position && (
        <span className={`ai-pick-finish tier-${m.finish_position <= 3 ? m.finish_position : "other"}`}
          data-testid="race-signal-finish">
          {m.finish_position}{finishSuffix(m.finish_position)}
        </span>
      )}
    </div>
  </div>
);

const StrongestSinglesRow = ({ m }) => (
  <div className="mm-row rs-row" data-testid="strongest-singles-row">
    <RankArrow m={m} testId="strongest-singles-arrow" />
    {m.silk_url ? <img className="mm-silk" src={m.silk_url} alt="" /> : <div className="mm-silk mm-silk-placeholder" />}
    <div className="mm-main">
      <span className="mm-name" data-testid="strongest-singles-name">#{m.tab_number} {m.runner_name}</span>
      <div className="mm-subline">{m.track} R{m.race_number}</div>
    </div>
    <div className="rs-signal-col">
      <span className="rs-signal-value" data-testid="strongest-singles-signal">{m.signal_score}</span>
      <span className="rs-band-chip" data-testid="strongest-singles-band">{m.signal_band}</span>
    </div>
    <div className="mm-firmed-col">
      <span className="rs-price-row">
        <span className="rs-price-text" data-testid="strongest-singles-price">${m.tab_price.toFixed(2)}</span>
        <TrajectoryIcon trajectory={m.trajectory} testId="strongest-singles-trajectory" />
      </span>
      <MoverCountdown isoUtc={m.race_time} />
      {m.finish_position && (
        <span className={`ai-pick-finish tier-${m.finish_position <= 3 ? m.finish_position : "other"}`}
          data-testid="strongest-singles-finish">
          {m.finish_position}{finishSuffix(m.finish_position)}
        </span>
      )}
    </div>
  </div>
);

const RaceSignalCard = ({ dateIso, isAuthenticated, onLoginClick }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [exMaidens, setExMaidens] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    const fetchData = () => {
      const params = { ex_maidens: exMaidens, ...(dateIso ? { date: dateIso } : {}) };
      axios.get(`${API}/race_signal_strength`, { params }).then((res) => {
        if (active) setData(res.data.entries || []);
      }).catch(() => { if (active) setData(null); });
    };
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => { active = false; clearInterval(interval); };
  }, [dateIso, exMaidens, isAuthenticated]);

  if (isAuthenticated && data && data.length === 0) return null;
  const visible = data && showAll ? data : (data || []).slice(0, 3);

  return (
    <div className="ai-scan-card mm-card" data-testid="race-signal-card">
      <button type="button" className="ai-scan-toggle" data-testid="race-signal-toggle" onClick={() => setExpanded((e) => !e)}>
        <span className="ai-scan-toggle-left">
          <LinkSimple size={18} weight="bold" color="#fff" />
          MULTI LEGS
        </span>
        <span className="ai-scan-toggle-right">
          {expanded ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
        </span>
      </button>
      {expanded && (
        <div className="ai-scan-body">
          {!isAuthenticated ? (
            <LoginGate feature="Multi legs" onLoginClick={onLoginClick} />
          ) : !data ? (
            <p className="ai-scan-empty" data-testid="race-signal-loading">Loading multi legs…</p>
          ) : (
            <div className="mm-section" data-testid="race-signal-section">
              <div className="mm-section-head">
                <span className="mm-section-title">STRONGEST MULTI LEGS</span>
                <label className="mm-maiden-toggle" data-testid="race-signal-ex-maidens">
                  <input type="checkbox" checked={exMaidens} onChange={(e) => setExMaidens(e.target.checked)} />
                  Ex. Maidens
                </label>
              </div>
              <div className="mm-table-head rs-table-head">
                <span></span><span></span><span>HORSE</span><span className="mm-th-right">SIGNAL</span><span className="mm-th-right">TAB</span>
              </div>
              <div className="mm-list">
                {visible.map((m, i) => <RaceSignalRow key={`${m.track}-${m.race_number}-${i}`} m={m} />)}
              </div>
              {data.length > 3 && (
                <button type="button" className="mm-expand-btn" data-testid="race-signal-expand"
                  onClick={() => setShowAll((s) => !s)}>
                  {showAll ? "Show less" : "Show more"}
                  {showAll ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const LiveEdgeCard = ({ dateIso, isAuthenticated, onLoginClick }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [exMaidens, setExMaidens] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    const fetchData = () => {
      const params = { ex_maidens: exMaidens, ...(dateIso ? { date: dateIso } : {}) };
      axios.get(`${API}/live_edge`, { params }).then((res) => {
        if (active) setData(res.data.entries || []);
      }).catch(() => { if (active) setData(null); });
    };
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => { active = false; clearInterval(interval); };
  }, [dateIso, exMaidens, isAuthenticated]);

  if (isAuthenticated && data && data.length === 0) return null;
  const visible = data && showAll ? data : (data || []).slice(0, 3);

  return (
    <div className="ai-scan-card mm-card" data-testid="live-edge-card">
      <button type="button" className="ai-scan-toggle" data-testid="live-edge-toggle" onClick={() => setExpanded((e) => !e)}>
        <span className="ai-scan-toggle-left">
          <Barbell size={18} weight="bold" color="#fff" />
          STRONGEST SINGLES
        </span>
        <span className="ai-scan-toggle-right">
          {expanded ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
        </span>
      </button>
      {expanded && (
        <div className="ai-scan-body">
          {!isAuthenticated ? (
            <LoginGate feature="Strongest singles" onLoginClick={onLoginClick} />
          ) : !data ? (
            <p className="ai-scan-empty" data-testid="live-edge-loading">Loading strongest singles…</p>
          ) : (
            <div className="mm-section" data-testid="live-edge-section">
              <div className="mm-section-head">
                <span className="mm-section-title">STRONGEST RACE SINGLES</span>
                <label className="mm-maiden-toggle" data-testid="live-edge-ex-maidens">
                  <input type="checkbox" checked={exMaidens} onChange={(e) => setExMaidens(e.target.checked)} />
                  Ex. Maidens
                </label>
              </div>
              <div className="mm-table-head rs-table-head">
                <span></span><span></span><span>HORSE</span><span className="mm-th-right">SIGNAL</span><span className="mm-th-right">TAB</span>
              </div>
              <div className="mm-list">
                {visible.map((m, i) => <StrongestSinglesRow key={`${m.track}-${m.race_number}-${m.tab_number}-${i}`} m={m} />)}
              </div>
              {data.length > 3 && (
                <button type="button" className="mm-expand-btn" data-testid="live-edge-expand" onClick={() => setShowAll((s) => !s)}>
                  {showAll ? "Show less" : "Show more"}
                  {showAll ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MarketMoversCard = ({ dateIso, isAuthenticated, onLoginClick }) => {
  const [shortData, setShortData] = useState(null);
  const [valueData, setValueData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [exMaidensShort, setExMaidensShort] = useState(false);
  const [exMaidensValue, setExMaidensValue] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    const fetchShort = () => {
      const params = { ex_maidens: exMaidensShort, ...(dateIso ? { date: dateIso } : {}) };
      axios.get(`${API}/market_movers`, { params }).then((res) => {
        if (active) setShortData(res.data.short || []);
      }).catch(() => { if (active) setShortData(null); }).finally(() => { if (active) setLoaded(true); });
    };
    fetchShort();
    const interval = setInterval(fetchShort, 120000);
    return () => { active = false; clearInterval(interval); };
  }, [dateIso, exMaidensShort, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    const fetchValue = () => {
      const params = { ex_maidens: exMaidensValue, ...(dateIso ? { date: dateIso } : {}) };
      axios.get(`${API}/market_movers`, { params }).then((res) => {
        if (active) setValueData(res.data.value || []);
      }).catch(() => { if (active) setValueData(null); });
    };
    fetchValue();
    const interval = setInterval(fetchValue, 120000);
    return () => { active = false; clearInterval(interval); };
  }, [dateIso, exMaidensValue, isAuthenticated]);

  if (isAuthenticated && loaded && (!shortData || shortData.length === 0) && (!valueData || valueData.length === 0)) return null;

  return (
    <div className="ai-scan-card mm-card" data-testid="market-movers-card">
      <button type="button" className="ai-scan-toggle" data-testid="market-movers-toggle" onClick={() => setExpanded((e) => !e)}>
        <span className="ai-scan-toggle-left">
          <CurrencyDollar size={18} weight="bold" color="#fff" />
          MARKET MOVERS
        </span>
        <span className="ai-scan-toggle-right">
          {expanded ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
        </span>
      </button>

      {expanded && (
        <div className="ai-scan-body">
          {!isAuthenticated ? (
            <LoginGate feature="Market movers" onLoginClick={onLoginClick} />
          ) : !shortData && !valueData ? (
            <p className="ai-scan-empty" data-testid="market-movers-loading">Loading movers…</p>
          ) : (
            <>
              <MoversTable title="SHORT" list={shortData || []} rightControl={
                <label className="mm-maiden-toggle" data-testid="market-movers-ex-maidens-short">
                  <input type="checkbox" checked={exMaidensShort} onChange={(e) => setExMaidensShort(e.target.checked)} />
                  Ex. Maidens
                </label>
              } />
              <MoversTable title="VALUE" list={valueData || []} rightControl={
                <label className="mm-maiden-toggle" data-testid="market-movers-ex-maidens-value">
                  <input type="checkbox" checked={exMaidensValue} onChange={(e) => setExMaidensValue(e.target.checked)} />
                  Ex. Maidens
                </label>
              } />
              <div className="ai-scan-legend mm-legend mm-legend-stack" data-testid="market-movers-legend">
                <span>Shortening &gt; Expected to shorten further.</span>
                <span>Holding &gt; Expected to remain stable.</span>
                <span>Easing &gt; Expected to drift back out.</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const StatsCard = ({ stats }) => {
  if (!stats) return null;
  const winStrike = stats.win_strike || 0;
  const isHot = winStrike >= 36;
  const isCold = winStrike < 28;
  const TempIcon = isHot ? Flame : (isCold ? Snowflake : null);
  const tempColor = isHot ? "#dc2626" : "#0891b2";
  return (
    <div className="stats-card" data-testid="stats-card">
      <div className="stats-header">
        <ChartLineUp size={20} weight="bold" color="#fff" />
        <h3>7-Day Performance</h3>
      </div>
      <div className="stats-grid">
        <div className="stat-item">
          <span className="stat-value" data-testid="num-selections">{stats.num_selections}</span>
          <span className="stat-label">Selections</span>
        </div>
        <div className="stat-item">
          <span className="stat-value" data-testid="num-winners">{stats.num_winners}</span>
          <span className="stat-label">Winners</span>
        </div>
        <div className="stat-item highlight profit" style={{ position: "relative" }}>
          {TempIcon && (
            <TempIcon
              size={16} weight="fill" color={tempColor}
              data-testid="temp-icon"
              style={{ position: "absolute", top: 8, right: 10 }}
            />
          )}
          <span className="stat-value" data-testid="win-strike">{winStrike.toFixed(1)}%</span>
          <span className="stat-label">Win Strike</span>
        </div>
        <div className="stat-item">
          <span className="stat-value" data-testid="avg-odds">${stats.average_dividend.toFixed(2)}</span>
          <span className="stat-label">Avg Odds</span>
        </div>
      </div>
    </div>
  );
};

const YtdStatsCard = ({ stats }) => {
  if (!stats) return null;
  return (
    <div className="stats-card" data-testid="ytd-stats-card" style={{ marginTop: 12 }}>
      <div className="stats-header">
        <CalendarBlank size={20} weight="bold" />
        <h3>Total Performance</h3>
      </div>
      <div className="stats-grid">
        <div className="stat-item">
          <span className="stat-value" data-testid="ytd-num-selections">{stats.num_selections}</span>
          <span className="stat-label">Selections</span>
        </div>
        <div className="stat-item">
          <span className="stat-value" data-testid="ytd-num-winners">{stats.num_winners}</span>
          <span className="stat-label">Winners</span>
        </div>
        <div className="stat-item highlight profit">
          <span className="stat-value" data-testid="ytd-win-strike">{stats.win_strike.toFixed(1)}%</span>
          <span className="stat-label">Win Strike</span>
        </div>
        <div className="stat-item">
          <span className="stat-value" data-testid="ytd-avg-odds">${stats.average_dividend.toFixed(2)}</span>
          <span className="stat-label">Avg Odds</span>
        </div>
      </div>
      <UnitsRow stats={stats} idPrefix="ytd" />
    </div>
  );
};

const ResultBadge = ({ position }) => {
  if (position == null) return null;
  let label, bg, color;
  if (position === 1) { label = "1st"; bg = "#D4AF37"; color = "#fff"; }
  else if (position === 2) { label = "2nd"; bg = "#A8A9AD"; color = "#fff"; }
  else if (position === 3) { label = "3rd"; bg = "#CD7F32"; color = "#fff"; }
  else { label = "Unplaced"; bg = "#e5e5e4"; color = "#888"; }
  return (
    <span data-testid="result-badge" style={{
      display: "inline-flex", alignItems: "center", gap: 3,
      padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600,
      background: bg, color, letterSpacing: "0.02em",
    }}>{label}</span>
  );
};

// Convert race time + state to a UTC-millis instant for sorting purposes.
function raceTimeToUtcMs(raceTime, state) {
  if (!raceTime || !raceTime.includes(":")) return Number.MAX_SAFE_INTEGER;
  const [h, m] = raceTime.split(":").map(Number);
  const tz = STATE_TZ[state] || "Australia/Melbourne";
  const now = Date.now();
  const trackOffsetMin = getOffsetMinutes(tz, now);
  const trackNow = new Date(now + trackOffsetMin * 60000);
  return Date.UTC(
    trackNow.getUTCFullYear(),
    trackNow.getUTCMonth(),
    trackNow.getUTCDate(),
    h, m, 0,
  ) - trackOffsetMin * 60000;
}

const RaceCountdown = ({ raceTime, state, dateString }) => {
  const [countdown, setCountdown] = useState(() => getCountdownToRace(raceTime, state, dateString));
  useEffect(() => {
    const t = setInterval(() => setCountdown(getCountdownToRace(raceTime, state, dateString)), 30000);
    return () => clearInterval(t);
  }, [raceTime, state, dateString]);
  if (!countdown) return null;
  const isUrgent = countdown === "LIVE" || /^\d+m$/.test(countdown); // <60m shows as "Nm"
  return (
    <span data-testid="race-countdown" style={{
      fontSize: 11, fontWeight: 600,
      color: isUrgent ? "#e53e3e" : "#888",
      background: isUrgent ? "#fee2e2" : "#f5f5f4",
      padding: "1px 6px", borderRadius: 4,
    }}>{countdown}</span>
  );
};

const REACTION_POSITIVE_SET = ["🔥", "🤞", "❤️", "👍", "🙌", "💪", "😍", "🎉", "⭐", "👏",
  "🏆", "💰", "🚀", "😎", "🤩", "👌", "💯", "🥳", "🍀", "✅"];
const REACTION_NEGATIVE_SET = ["👎", "💩", "😢", "😡", "🤢", "💀", "🙄", "😴", "⚠️", "❌"];
const REACTION_ADD_SET = [...REACTION_POSITIVE_SET, ...REACTION_NEGATIVE_SET];

const ReactionRow = ({ selection, index, adminToken }) => {
  const isAdmin = !!adminToken;
  const [counts, setCounts] = useState({});
  const reactKey = `rxn_${selection.date_string}_${selection.track}_${selection.race_number}_${selection.horse_name}`
    .replace(/\s+/g, "_").toLowerCase();
  const [myEmoji, setMyEmoji] = useState(() => localStorage.getItem(reactKey) || null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/reactions`, { params: {
      date_string: selection.date_string, track: selection.track,
      race_number: selection.race_number, horse_name: selection.horse_name,
    } }).then((res) => { if (active) setCounts(res.data.counts || {}); }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.date_string, selection.track, selection.race_number, selection.horse_name]);

  const react = (emoji) => {
    if (isAdmin) {
      // Admin: unlimited plain increments, no lock, no switching —
      // can stack as many emojis (and re-clicks) as they like.
      setCounts((c) => ({ ...c, [emoji]: (c[emoji] || 0) + 1 }));
      axios.post(`${API}/reactions/react`, {
        date_string: selection.date_string, track: selection.track,
        race_number: selection.race_number, horse_name: selection.horse_name, emoji,
      }).catch(() => {});
      return;
    }
    if (emoji === myEmoji) { setPickerOpen(false); return; }
    const previous = myEmoji;
    setCounts((c) => {
      const next = { ...c, [emoji]: (c[emoji] || 0) + 1 };
      if (previous) next[previous] = Math.max(0, (c[previous] || 0) - 1);
      return next;
    });
    setMyEmoji(emoji);
    localStorage.setItem(reactKey, emoji);
    setPickerOpen(false);
    axios.post(`${API}/reactions/react`, {
      date_string: selection.date_string, track: selection.track,
      race_number: selection.race_number, horse_name: selection.horse_name,
      emoji, previous_emoji: previous,
    }).catch(() => {});
  };

  const topEmojis = Object.entries(counts).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]).slice(0, 8);

  return (
    <div className="reaction-row" data-testid={`reaction-row-${index}`}>
      {topEmojis.map(([emoji, count]) => (
        <button type="button" key={emoji} className={`reaction-chip ${emoji === myEmoji ? "reaction-chip-mine" : ""}`}
          data-testid={`reaction-chip-${index}-${emoji}`} onClick={() => react(emoji)}>
          <span className="reaction-emoji">{emoji}</span><span className="reaction-chip-count">{count}</span>
        </button>
      ))}
      <div className="reaction-picker-wrap">
        <button type="button" className="reaction-more-btn" data-testid={`reaction-more-${index}`}
          onClick={() => setPickerOpen((p) => !p)}>…</button>
      </div>
      {pickerOpen && (
        <div className="reaction-picker" data-testid={`reaction-picker-${index}`}>
          {REACTION_ADD_SET.map((emoji) => (
            <button type="button" key={emoji} className="reaction-picker-option"
              data-testid={`reaction-picker-option-${index}-${emoji}`}
              onClick={() => react(emoji)}>{emoji}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const HorseCard = ({ selection, index, isValueBet, adminToken, onSelectionUpdated }) => {
  const [expanded, setExpanded] = useState(false);
  // V7.4.8 — admin inline edit state.  When the logged-in admin clicks
  // the pencil on a card, we snapshot the current blurb / confidence /
  // unit_stake into editable buffers and only commit on Save.  Cancel
  // discards.  Backend PATCH /admin/selections/{id} also sets
  // is_admin_edited=true so the scrape jobs preserve the override.
  const [editing, setEditing] = useState(false);
  const [editBlurb, setEditBlurb] = useState(selection.blurb || "");
  const [editConf, setEditConf] = useState(selection.confidence ?? 0);
  const [editStake, setEditStake] = useState(selection.unit_stake ?? 1);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  // V7.4.9 — admin "remove" affordance state.  Two-step click so a
  // mis-tap can't nuke a horse: first click arms the X (confirmation
  // chip appears), second click within 4 seconds actually fires the
  // DELETE.  The pending state auto-cancels after the window expires.
  const [removing, setRemoving] = useState(false);
  const [removeArmed, setRemoveArmed] = useState(false);
  const [removed, setRemoved] = useState(false);
  useEffect(() => {
    if (!removeArmed) return;
    const t = setTimeout(() => setRemoveArmed(false), 4000);
    return () => clearTimeout(t);
  }, [removeArmed]);
  // Re-sync editable buffers when the selection prop changes (e.g.
  // a background refresh) — but only when we're NOT mid-edit so the
  // admin's in-progress typing isn't blown away.
  useEffect(() => {
    if (editing) return;
    setEditBlurb(selection.blurb || "");
    setEditConf(selection.confidence ?? 0);
    setEditStake(selection.unit_stake ?? 1);
  }, [selection.id, selection.blurb, selection.confidence,
      selection.unit_stake, editing]);

  const confidenceClass = selection.confidence >= 70 ? "high"
    : selection.confidence >= 55 ? "med" : "low";
  const localTime = raceTimeToLocal(selection.race_time, selection.state);

  const saveEdit = async () => {
    if (!adminToken) return;
    setSaving(true); setSaveErr("");
    try {
      const { data } = await axios.patch(
        `${API}/admin/selections/${selection.id}`,
        {
          blurb: editBlurb,
          confidence: Number(editConf),
          unit_stake: Number(editStake),
        },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      setEditing(false);
      // Notify parent so the bet-slip list re-renders with the
      // freshly-saved values instantly (no need to wait for the
      // 60-second poll).
      if (data?.selection && onSelectionUpdated) {
        onSelectionUpdated(data.selection);
      }
    } catch (err) {
      setSaveErr(
        err?.response?.data?.detail ||
        err?.message || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };
  const cancelEdit = () => {
    setEditing(false); setSaveErr("");
    setEditBlurb(selection.blurb || "");
    setEditConf(selection.confidence ?? 0);
    setEditStake(selection.unit_stake ?? 1);
  };
  const bumpStake = (delta) => {
    const next = Math.max(0, Math.min(100,
      Math.round((Number(editStake) + delta) * 100) / 100));
    setEditStake(next);
  };

  // V7.4.9 — DELETE the selection.  Two-step confirm — first click
  // arms the chip, second click fires.  On success we fade the card
  // visually and tell the parent to refresh so the row drops out of
  // the list naturally.
  const handleRemove = async (e) => {
    e.stopPropagation();
    if (!adminToken || removing || removed) return;
    if (!removeArmed) {
      setRemoveArmed(true);
      return;
    }
    setRemoving(true); setSaveErr("");
    try {
      await axios.delete(
        `${API}/admin/selections/${selection.id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      setRemoved(true);
      // Tell parent to re-fetch the selections list so the card
      // disappears from the rendered grid.
      if (onSelectionUpdated) onSelectionUpdated({ id: selection.id, _deleted: true });
    } catch (err) {
      setSaveErr(
        err?.response?.data?.detail || err?.message || "Remove failed",
      );
      setRemoveArmed(false);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div
      className={`horse-card ${selection.is_best_bet || isValueBet ? 'best-bet' : ''}`}
      data-testid={`horse-card-${index}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {selection.is_best_bet && (
        <div className="best-bet-badge" data-testid="best-bet-badge">
          <Trophy size={12} weight="fill" style={{ marginRight: 4 }} />Best Bet
        </div>
      )}
      {isValueBet && !selection.is_best_bet && (
        <div className="best-bet-badge" data-testid="value-bet-badge">
          <HandCoins size={14} weight="fill" style={{ marginRight: 4 }} />Value Bet
        </div>
      )}
      {/* V7.4.8 / V7.4.9 — admin controls (only rendered when the
          logged-in admin token is present).  Pencil opens the
          inline editor.  Red X armed-confirm deletes the
          selection entirely (two clicks within 4 seconds).  Sits
          at top-left so it never collides with the price corner.
          The "edited" dot below the pencil reassures the admin
          that the AI auto-rewriter is locked out for this card. */}
      {adminToken && !editing && !removed && (
        <div style={{
          position: "absolute", top: 8, left: 8,
          display: "inline-flex", alignItems: "center", gap: 4,
          zIndex: 2,
        }}>
          <button
            type="button"
            data-testid={`admin-edit-${index}`}
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            title={selection.is_admin_edited
              ? "Edit (locked from AI rewrites)"
              : "Edit blurb / confidence / stake"}
            style={{
              background: selection.is_admin_edited ? "#dbeafe" : "#f4f4f3",
              border: `1px solid ${selection.is_admin_edited ? "#3b82f6" : "#d4d4d3"}`,
              borderRadius: 6,
              padding: "4px 6px",
              cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 11, color: "#1f2937", fontWeight: 600,
            }}
          >
            <PencilSimple size={12} weight="bold" />
            {selection.is_admin_edited && (
              <span style={{
                width: 5, height: 5, borderRadius: "50%",
                background: "#3b82f6", display: "inline-block",
              }} />
            )}
          </button>
          <button
            type="button"
            data-testid={`admin-remove-${index}`}
            onClick={handleRemove}
            disabled={removing}
            title={removeArmed
              ? "Click again to confirm removal"
              : "Remove this selection from the bet slip"}
            style={{
              background: removeArmed ? "#dc2626" : "#fef2f2",
              border: `1px solid ${removeArmed ? "#991b1b" : "#fecaca"}`,
              borderRadius: 6,
              padding: removeArmed ? "4px 10px" : "4px 6px",
              cursor: removing ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 11, fontWeight: 700,
              color: removeArmed ? "#fff" : "#dc2626",
              transition: "background 0.15s, padding 0.15s",
            }}
          >
            <X size={12} weight="bold" />
            {removeArmed && (
              <span data-testid={`admin-remove-confirm-${index}`}>
                {removing ? "…" : "Confirm"}
              </span>
            )}
          </button>
        </div>
      )}

      <div className="horse-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10, paddingRight: 95, minHeight: 32 }}>
          {selection.silk_url && (
            <img src={selection.silk_url} alt="" data-testid={`silk-${index}`}
              style={{ width: 32, height: 32, borderRadius: 4, objectFit: "cover", flexShrink: 0 }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 className="horse-name" data-testid={`horse-name-${index}`}>
              {selection.tab_number > 0 ? `#${selection.tab_number} ` : ""}{selection.horse_name}
            </h3>
          </div>
        </div>
        <div style={{
          position: "absolute", top: 0, right: 0,
          display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4,
        }}>
          <div className="horse-price" data-testid={`horse-price-${index}`}>${selection.current_price.toFixed(2)}</div>
          {(() => {
            // Drift/firm badge driven by TOTAL move from opening_price, so it
            // stays visible as long as the horse remains firmed (not just for
            // the single scrape window where the move occurred).
            const open = selection.opening_price;
            const curr = selection.current_price;
            if (open == null || curr == null) return null;
            const totalMove = curr - open;            // negative = firmed
            if (Math.abs(totalMove) < 0.20) return null;
            const firmed = totalMove < 0;
            const pct = (Math.abs(totalMove) / open) * 100;
            return (
              <div data-testid={`price-trend-${index}`} style={{
                display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600,
                color: firmed ? "#059669" : "#dc2626",
                background: firmed ? "#ecfdf5" : "#fef2f2",
                padding: "2px 7px", borderRadius: 4, letterSpacing: "0.02em",
                whiteSpace: "nowrap",
              }}>
                {firmed ? <CaretDown size={10} weight="bold" /> : <CaretUp size={10} weight="bold" />}
                ${Math.abs(totalMove).toFixed(2)}
                {firmed && pct >= 30 && <span data-testid={`hot-fire-${index}`}>🔥🔥🔥</span>}
                {firmed && pct >= 20 && pct < 30 && <span data-testid={`hot-fire-${index}`}>🔥🔥</span>}
                {firmed && pct >= 10 && pct < 20 && <span data-testid={`hot-fire-${index}`}>🔥</span>}
              </div>
            );
          })()}
          {selection.opening_price != null
            && Math.abs((selection.opening_price - selection.current_price)) >= 0.01 && (
            <div data-testid={`opening-price-${index}`} style={{
              fontSize: 10, fontWeight: 500, color: "#999",
              whiteSpace: "nowrap", letterSpacing: "0.02em",
            }}>
              selected at ${selection.opening_price.toFixed(2)}
            </div>
          )}
          <ResultBadge position={selection.finish_position} />
        </div>
      </div>

      {(selection.jockey || selection.trainer) && (
        <div data-testid={`connections-${index}`} style={{
          display: "flex", gap: 12, fontSize: 12, color: "#888",
          padding: "0 0 4px", letterSpacing: "0.01em", flexWrap: "wrap",
        }}>
          {selection.jockey && <span><span style={{ fontWeight: 600, color: "#aaa" }}>J</span> {selection.jockey}</span>}
          {selection.trainer && <span><span style={{ fontWeight: 600, color: "#aaa" }}>T</span> {selection.trainer}</span>}
        </div>
      )}

      <div className="race-info" data-testid={`race-info-${index}`} style={{
        display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 2,
      }}>
        <MapPin size={14} weight="fill" />
        <span>{localTime} - {selection.state} {selection.track} R{selection.race_number}</span>
        <RaceCountdown raceTime={selection.race_time} state={selection.state} dateString={selection.date_string} />
      </div>

      {(selection.distance || selection.track_condition || selection.race_class || selection.field_size) && (
        <div data-testid={`race-details-${index}`} style={{
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          fontSize: 11, color: "#888", padding: "0 0 10px", paddingLeft: 20,
          letterSpacing: "0.02em",
        }}>
          {selection.distance && <span data-testid={`race-distance-${index}`}>{selection.distance}</span>}
          {selection.track_condition && (
            <>
              <span style={{ color: "#ddd" }}>·</span>
              <span data-testid={`race-condition-${index}`}>{selection.track_condition}</span>
            </>
          )}
          {selection.race_class && (
            <>
              <span style={{ color: "#ddd" }}>·</span>
              <span data-testid={`race-class-${index}`}>{selection.race_class}</span>
            </>
          )}
          {selection.field_size > 0 && (
            <>
              <span style={{ color: "#ddd" }}>·</span>
              <span data-testid={`race-field-size-${index}`}>{selection.field_size} runners</span>
            </>
          )}
          {selection.barrier && (
            <>
              <span style={{ color: "#ddd" }}>·</span>
              <span data-testid={`race-barrier-${index}`}>Bar {selection.barrier}</span>
            </>
          )}
          {selection.weight && (
            <>
              <span style={{ color: "#ddd" }}>·</span>
              <span data-testid={`race-weight-${index}`}>{selection.weight}kg</span>
            </>
          )}
          {selection.running_style && (
            <>
              <span style={{ color: "#ddd" }}>·</span>
              <span data-testid={`race-running-style-${index}`}>{selection.running_style}</span>
            </>
          )}
        </div>
      )}

      <div className="horse-blurb-container" data-testid={`horse-blurb-${index}`}>
        {editing ? (
          <textarea
            data-testid={`admin-edit-blurb-${index}`}
            value={editBlurb}
            onChange={(e) => setEditBlurb(e.target.value)}
            rows={3}
            placeholder="Card narrative (shown to subscribers)…"
            style={{
              width: "100%", boxSizing: "border-box",
              border: "1px solid #3b82f6", borderRadius: 6,
              padding: "8px 10px", fontSize: 13, lineHeight: 1.5,
              fontFamily: "inherit", resize: "vertical",
              background: "#fff",
            }}
          />
        ) : (
          <p className="horse-blurb">{selection.blurb}</p>
        )}
        {selection.detailed_blurb && !editing && (
          <>
            <button data-testid={`read-more-${index}`} onClick={() => setExpanded(!expanded)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#323233",
                fontSize: 12, fontWeight: 600, padding: "4px 0", letterSpacing: "0.03em",
                opacity: 0.6, transition: "opacity 0.2s" }}
              onMouseEnter={(e) => { e.target.style.opacity = 1; }}
              onMouseLeave={(e) => { e.target.style.opacity = 0.6; }}
            >{expanded ? 'Show Less' : 'Read More'}</button>
            {expanded && (
              <p data-testid={`detailed-blurb-${index}`} style={{
                fontSize: 12, lineHeight: 1.7, color: "#666", margin: "6px 0 0", padding: "8px 10px",
                background: "#f9f9f8", borderRadius: 6, borderLeft: "2px solid #ddd",
              }}>{selection.detailed_blurb}</p>
            )}
          </>
        )}
        {selection.badges && selection.badges.length > 0 && (
          <div className="selection-badges" data-testid={`selection-badges-${index}`}>
            {aiDisplayBadges(selection.badges, isWetCondition(selection.track_condition)).map(({ raw, cfg }, i) => {
              const Icon = cfg.icon;
              return (
                <span key={i} className={`ai-pick-badge-chip badge-tier-${cfg.tier}`}>
                  <Icon size={8} weight="bold" />{raw}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="card-footer">
        <div className="confidence-section">
          <div className="confidence-header">
            <Target size={14} weight="bold" />
            <span className="confidence-label">Confidence</span>
            <span className={`confidence-value confidence-${editing
              ? (editConf >= 70 ? "high" : editConf >= 55 ? "med" : "low")
              : confidenceClass}`}
              data-testid={`confidence-${index}`}>
              {editing ? Math.round(editConf) : selection.confidence}%
            </span>
          </div>
          {editing ? (
            <input
              type="range"
              min="0" max="100" step="1"
              value={editConf}
              data-testid={`admin-edit-confidence-${index}`}
              onChange={(e) => setEditConf(Number(e.target.value))}
              style={{ width: "100%", marginTop: 4 }}
            />
          ) : (
            <div className="confidence-bar">
              <div className={`confidence-bar-fill ${confidenceClass}`}
                style={{ width: `${selection.confidence}%` }}></div>
            </div>
          )}
        </div>
        <ReactionRow selection={selection} index={index} adminToken={adminToken} />
        <div className="stakes-section">
          <TrendingUp size={16} />
          <span className="stakes-label">Suggested Stake:</span>
          {editing ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <button type="button"
                data-testid={`admin-stake-minus-${index}`}
                onClick={() => bumpStake(-0.05)}
                style={{
                  border: "1px solid #d4d4d3", background: "#fff",
                  borderRadius: 4, width: 24, height: 24,
                  display: "inline-flex", alignItems: "center",
                  justifyContent: "center", cursor: "pointer",
                }}>
                <Minus size={11} weight="bold" />
              </button>
              <input
                type="number"
                step="0.05" min="0" max="100"
                value={editStake}
                data-testid={`admin-edit-stake-${index}`}
                onChange={(e) => setEditStake(Number(e.target.value))}
                style={{
                  width: 56, textAlign: "center",
                  border: "1px solid #d4d4d3", borderRadius: 4,
                  padding: "2px 4px", fontSize: 13, fontWeight: 600,
                }}
              />
              <button type="button"
                data-testid={`admin-stake-plus-${index}`}
                onClick={() => bumpStake(0.05)}
                style={{
                  border: "1px solid #d4d4d3", background: "#fff",
                  borderRadius: 4, width: 24, height: 24,
                  display: "inline-flex", alignItems: "center",
                  justifyContent: "center", cursor: "pointer",
                }}>
                <Plus size={11} weight="bold" />
              </button>
              <span style={{ color: "#888", fontSize: 12 }}>
                Unit{editStake !== 1 ? 's' : ''}
              </span>
            </span>
          ) : (
            <span className="stakes-value" data-testid={`stake-${index}`}>
              {selection.unit_stake} Unit{selection.unit_stake !== 1 ? 's' : ''}
              {selection.bet_type && (
                <span data-testid={`bet-type-${index}`} style={{ color: "#888", marginLeft: 6, fontWeight: 500 }}>
                  ({selection.bet_type})
                </span>
              )}
            </span>
          )}
        </div>
        {selection.stake_split && (selection.stake_split.win > 0 && selection.stake_split.place > 0) && !editing && (
          <div data-testid={`stake-split-${index}`} style={{
            fontSize: 11, color: "#999", padding: "0 0 0 22px", marginTop: -6, marginBottom: 4,
            letterSpacing: "0.01em",
          }}>
            {(selection.unit_stake * selection.stake_split.win).toFixed(2)} Win / {(selection.unit_stake * selection.stake_split.place).toFixed(2)} Place
          </div>
        )}
        {/* V7.4.8 — admin action row.  Sits at the bottom of the card
            in edit mode so the Save / Cancel buttons are always
            visible regardless of textarea length. */}
        {editing && (
          <div style={{
            display: "flex", gap: 8, marginTop: 10,
            paddingTop: 10, borderTop: "1px dashed #d4d4d3",
            alignItems: "center", justifyContent: "flex-end",
          }}>
            {saveErr && (
              <span data-testid={`admin-edit-error-${index}`} style={{
                color: "#dc2626", fontSize: 11, marginRight: "auto",
              }}>{saveErr}</span>
            )}
            <button type="button"
              data-testid={`admin-cancel-${index}`}
              onClick={cancelEdit} disabled={saving}
              style={{
                border: "1px solid #d4d4d3", background: "#fff",
                borderRadius: 6, padding: "6px 12px",
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: 12, fontWeight: 600, color: "#374151",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}>
              <X size={12} weight="bold" /> Cancel
            </button>
            <button type="button"
              data-testid={`admin-save-${index}`}
              onClick={saveEdit} disabled={saving}
              style={{
                border: "1px solid #1f2937", background: "#1f2937",
                color: "#fff", borderRadius: 6, padding: "6px 14px",
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: 12, fontWeight: 700, letterSpacing: "0.02em",
                display: "inline-flex", alignItems: "center", gap: 4,
                opacity: saving ? 0.6 : 1,
              }}>
              <FloppyDisk size={12} weight="bold" />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Disclaimer = ({ onAdminTrigger }) => (
  <footer data-testid="disclaimer" style={{
    padding: "16px 16px 100px", textAlign: "center", fontSize: 11, lineHeight: 1.8,
    color: "#bbb", maxWidth: 600, margin: "0 auto",
  }}>
    <p style={{ marginBottom: 8, fontWeight: 500, color: "#aaa" }}>
      Gambling involves risk. Please gamble responsibly.
    </p>
    <p style={{ marginBottom: 8 }}>
      If you or someone you know needs help, call 1800 858 858 or visit the{' '}
      <a href="https://www.gamblinghelponline.org.au/" target="_blank" rel="noopener noreferrer"
        style={{ color: "#999", textDecoration: "underline" }}>National Gambling Helpline</a>.
    </p>
    <p style={{ marginBottom: 8 }}>
      The information provided on this site is for informational and entertainment purposes only and does not
      constitute financial or betting advice. No guarantees are made regarding the accuracy or profitability
      of any selections.
    </p>
    <p style={{ marginBottom: 8 }}>Past performance is not a reliable indicator of future results.</p>
    <p style={{ color: "#ccc" }}>2026 FTP. All rights reserved.</p>
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
      {onAdminTrigger}
    </div>
    {/* Version tag — auto-bumps each deploy (reads /api/version which
        reads /app/backend/VERSION). Useful to confirm a redeploy has
        actually landed. Tiny + faded so it doesn't visually compete. */}
    <BuildVersionTag />
  </footer>
);

const BuildVersionTag = () => {
  const [v, setV] = useState("");
  useEffect(() => {
    axios.get(`${API}/version`).then(r => setV(r?.data?.version || "")).catch(() => {});
  }, []);
  if (!v) return null;
  return (
    <div data-testid="build-version-tag" style={{
      marginTop: 12, fontSize: 9, color: "#bbb", letterSpacing: "0.08em",
      textTransform: "uppercase", fontVariantNumeric: "tabular-nums",
    }}>
      {v}
    </div>
  );
};

const WinnersFooter = () => {
  const [winners, setWinners] = useState(null);

  useEffect(() => {
    let active = true;
    const fetchData = () => {
      axios.get(`${API}/daily_winners`).then((res) => {
        if (active) setWinners(res.data.winners || []);
      }).catch(() => {});
    };
    fetchData();
    const interval = setInterval(fetchData, 180000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  if (!winners || winners.length === 0) return null;

  const renderItems = (prefix) => winners.map((w, i) => (
    <span className="winners-footer-item" key={`${prefix}-${i}`}>
      {w.silk_url
        ? <img className="winners-footer-silk" src={w.silk_url} alt="" />
        : <span className="winners-footer-silk winners-footer-silk-placeholder" />}
      <span className="winners-footer-info">
        <span className="winners-footer-name" data-testid="winners-footer-name">{w.runner_name}</span>
        <span className="winners-footer-track">{w.track} R{w.race_number}</span>
      </span>
      {w.tab_price != null && <span className="winners-footer-price">${w.tab_price.toFixed(2)}</span>}
      <span className="winners-footer-badge">1ST</span>
    </span>
  ));

  // Slow, steady pace regardless of how many winners there are today.
  const duration = Math.max(30, winners.length * 5);

  return (
    <div className="winners-footer" data-testid="winners-footer">
      <div className="winners-footer-scroll" style={{ animationDuration: `${duration}s` }}>
        {renderItems("a")}
        {renderItems("b")}
      </div>
    </div>
  );
};


// ============== Main App ==============

function App() {
  const {
    selections, bestBet, valueBets, loading, error, lastUpdated,
    melbourneDate, nextRefresh, refreshing, weeklyStats, ytdStats,
    viewingDay, selectDay,
    fetchSelections, handleSync,
  } = useSelections();
  const { user, logout } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  // Fire-and-forget visit tracking (deduped 30 min server-side per path+visitor).
  useEffect(() => {
    axios.post(`${API}/track/visit`, {
      path: window.location.pathname || "/",
      referrer: document.referrer || "",
    }).catch(() => {});
  }, []);

  const [sortBy, setSortBy] = useState("confidence");

  // V7.4.8 — admin token state.  Polled lightly so that logging in
  // through the AdminPortal modal unlocks the public-card edit
  // pencils without a hard refresh, and logging out hides them.
  const [adminToken, setAdminToken] = useState(() => readAdminToken());
  useEffect(() => {
    const tick = () => setAdminToken(readAdminToken());
    window.addEventListener("storage", tick);
    const id = setInterval(tick, 2000);
    return () => {
      window.removeEventListener("storage", tick);
      clearInterval(id);
    };
  }, []);

  // A logged-in admin already has full access via the AdminPortal --
  // don't also gate/blur the public cards behind an end-user login.
  const isAuthenticated = !!user || !!adminToken;

  // V7.4.8 — when an admin saves an inline edit, merge the freshly-
  // returned selection back into local state so the card re-renders
  // instantly (and the badge / sort order updates correctly).
  const handleSelectionUpdated = useCallback((updated) => {
    if (!updated || !updated.id) return;
    // Re-fetch in the background to pick up any derived recompute
    // on the backend (e.g. is_best_bet shuffling).  Silent — the
    // PATCH response already returned the saved doc and the inline
    // re-render happens before this resolves.
    fetchSelections().catch(() => {});
  }, [fetchSelections]);

  const otherSelections = useMemo(() => {
    const others = selections.filter(s => !s.is_best_bet);
    if (sortBy === "jump") {
      return [...others].sort((a, b) =>
        raceTimeToUtcMs(a.race_time, a.state) - raceTimeToUtcMs(b.race_time, b.state)
      );
    }
    if (sortBy === "stake") {
      return [...others].sort((a, b) => b.unit_stake - a.unit_stake || b.confidence - a.confidence);
    }
    return others; // Already sorted by confidence from API
  }, [selections, sortBy]);

  const sortedValueBets = useMemo(() => {
    if (sortBy === "jump") {
      return [...valueBets].sort((a, b) =>
        raceTimeToUtcMs(a.race_time, a.state) - raceTimeToUtcMs(b.race_time, b.state)
      );
    }
    return valueBets;
  }, [valueBets, sortBy]);

  const hasContent = selections.length > 0 || valueBets.length > 0;

  return (
    <div className="app-container" data-testid="app-container">
      {/* Header */}
      <header className="header" data-testid="header">
        <div className="header-content">
          <img src={LOGO_URL} alt="FTP" className="logo" data-testid="logo" />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CalendarPicker
              selectedDate={viewingDay?.date}
              onSelectDate={selectDay}
              displayLabel={melbourneDate ? formatShortDate(melbourneDate) : "Loading..."}
            />
            {user ? (
              <button type="button" onClick={logout} data-testid="header-logout-button" title={user.email}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5, background: "none",
                  border: "1px solid #e5e5e4", borderRadius: 99, padding: "6px 10px",
                  cursor: "pointer", color: "#888", fontSize: 11, fontWeight: 600,
                }}>
                <SignOut size={13} weight="bold" />Log out
              </button>
            ) : (
              <button type="button" onClick={() => setShowAuth(true)} data-testid="header-login-button"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5, background: "#323233",
                  border: "none", borderRadius: 99, padding: "7px 12px",
                  cursor: "pointer", color: "#fff", fontSize: 11, fontWeight: 700,
                }}>
                Log in
              </button>
            )}
          </div>
        </div>
      </header>

      {showAuth && <AuthPage onClose={() => setShowAuth(false)} />}

      {/* Calendar viewing banner — only when not on Today */}
      <ViewingDateBanner day={viewingDay} onBackToToday={() => selectDay(null)} />

      {/* Main Content */}
      <main className="main-content">
        {loading && (
          <div className="loading-container" data-testid="loading-container">
            <LoadingCard /><LoadingCard /><LoadingCard />
          </div>
        )}

        {error && !loading && <ErrorState onRetry={fetchSelections} />}

        {!loading && !error && (
          <div style={{ marginBottom: 10 }}>
            <PushBell />
          </div>
        )}

        {!loading && !error && <IntroCard />}

        {!loading && !error && <NextToJumpCard />}

        {!loading && !error && <AIScanCard dateIso={ddmmyyyyToIso(viewingDay?.date)} isAuthenticated={isAuthenticated} onLoginClick={() => setShowAuth(true)} />}

        {!loading && !error && <RaceSignalCard dateIso={ddmmyyyyToIso(viewingDay?.date)} isAuthenticated={isAuthenticated} onLoginClick={() => setShowAuth(true)} />}

        {!loading && !error && <LiveEdgeCard dateIso={ddmmyyyyToIso(viewingDay?.date)} isAuthenticated={isAuthenticated} onLoginClick={() => setShowAuth(true)} />}

        {!loading && !error && <MarketMoversCard dateIso={ddmmyyyyToIso(viewingDay?.date)} isAuthenticated={isAuthenticated} onLoginClick={() => setShowAuth(true)} />}

        {!loading && !error && (weeklyStats || ytdStats) && (
          <div className="staking-guidance-divider" data-testid="official-staking-header">
            <span className="staking-guidance-line" />
            <span className="staking-guidance-label">Official Staking Guidance</span>
            <span className="staking-guidance-line" />
          </div>
        )}
        {!loading && !error && weeklyStats && <StatsCard stats={weeklyStats} />}
        {!loading && !error && ytdStats && <YtdStatsCard stats={ytdStats} />}

        {!loading && !error && (
          <div className="status-bar" data-testid="status-bar">
            <div className="status-item">
              <span className="status-label">Selections</span>
              <span className="status-value" data-testid="selections-count">{selections.length + valueBets.length}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Last Updated</span>
              <span className="status-value" data-testid="last-updated">{lastUpdated || "--:--"}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Next Refresh</span>
              <span className="status-value" data-testid="countdown">{formatCountdown(nextRefresh)}</span>
            </div>
          </div>
        )}

        {!loading && !error && !hasContent && <EmptyState />}

        {/* Unit staking explainer — shown whenever selections exist */}
        {!loading && !error && hasContent && <UnitStakingExplainer />}

        {/* Official bet slips — blurred with a login overlay until authenticated */}
        {!loading && !error && (bestBet || sortedValueBets.length > 0 || otherSelections.length > 0) && (
          <div style={{ position: "relative" }}>
            {!isAuthenticated && (
              <div className="betslip-gate-overlay" data-testid="betslip-gate-overlay">
                <button type="button" className="login-gate-button" data-testid="betslip-login-button" onClick={() => setShowAuth(true)}>
                  Log in to view<CaretRight size={12} weight="bold" />
                </button>
              </div>
            )}
            <div style={isAuthenticated ? undefined : { filter: "blur(7px)", pointerEvents: "none", userSelect: "none" }}>
              {/* Best Bet */}
              {bestBet && (
                <>
                  <div className="section-header">
                    <Trophy size={18} weight="fill" color="#323233" />
                    <h2 className="section-title">Best Bet</h2>
                  </div>
                  <HorseCard selection={bestBet} index={0} isValueBet={false}
                    adminToken={adminToken} onSelectionUpdated={handleSelectionUpdated} />
                </>
              )}

              {/* Value Bets */}
              {sortedValueBets.length > 0 && (
                <>
                  <div className="section-header" style={{ marginTop: 8 }}>
                    <HandCoins size={20} weight="fill" color="#323233" />
                    <h2 className="section-title">Value Bet{sortedValueBets.length > 1 ? 's' : ''}</h2>
                  </div>
                  <p data-testid="value-bet-disclaimer" style={{
                    fontSize: 11, color: "#aaa", margin: "-4px 0 8px", fontStyle: "italic",
                  }}>
                    Value bets are not reflected in the above ROI statistics.
                  </p>
                  {sortedValueBets.map((sel, i) => (
                    <HorseCard key={sel.id} selection={sel} index={100 + i} isValueBet={true}
                      adminToken={adminToken} onSelectionUpdated={handleSelectionUpdated} />
                  ))}
                </>
              )}

              {/* Sort dropdown — positioned between Value Bets and Other Selections */}
              {otherSelections.length > 0 && (
                <div data-testid="sort-bar" style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "12px 0 4px",
                  fontSize: 12, color: "#888",
                }}>
                  <SortAscending size={14} weight="bold" />
                  <span>Sort by:</span>
                  <select data-testid="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      background: "#f5f5f4", border: "1px solid #e5e5e4", borderRadius: 6,
                      padding: "4px 8px", fontSize: 12, color: "#323233", cursor: "pointer",
                    }}>
                    <option value="confidence">Confidence</option>
                    <option value="jump">Next to Jump</option>
                    <option value="stake">Unit Stake</option>
                  </select>
                </div>
              )}

              {/* Other Selections */}
              {otherSelections.length > 0 && (
                <>
                  <div className="section-header">
                    <Horse size={18} weight="fill" color="#323233" />
                    <h2 className="section-title">Other Selections</h2>
                  </div>
                  {otherSelections.map((sel, i) => (
                    <HorseCard key={sel.id} selection={sel} index={i + 1} isValueBet={false}
                      adminToken={adminToken} onSelectionUpdated={handleSelectionUpdated} />
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Daily Winners Footer */}
      <WinnersFooter />

      {/* Disclaimer */}
      <Disclaimer onAdminTrigger={<AdminPortal onDataChanged={fetchSelections} />} />
    </div>
  );
}

export default App;
